import { useMemo, useState, useEffect } from 'react'

import {
  Divider,
  MultiSelect,
  NumberInput,
  Select,
  TagsInput,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core'

import { useQuery } from '@redwoodjs/web'

import { useAuth } from 'src/auth'
import { CollapsibleSection } from 'src/components/CollapsibleSection'
import { GET_EMPLOYEES_ONLY } from 'src/graphql/employees.graphql'
import { GET_JOB_LISTING_TEMPLATES } from 'src/graphql/jobListingTemplates.graphql'
import { EmployeeStatus } from 'src/graphql/types/employees'
import {
  EmploymentTypeLabels,
  employmentTypeOptions,
  JobListingType,
  jobListingTypeOptions,
  payUnitOptions,
} from 'src/graphql/types/jobListings'
import {
  endorsementOptions,
  LicenseClassLabels,
  licenseClassOptions,
} from 'src/graphql/types/licenses'
import IconInfoCircle from 'src/icons/IconInfoCircle'
import { formatAddress } from 'src/lib/address.utils'
import { formatEnum } from 'src/lib/formatters'
import {
  buildJobListingFormValues,
  formatPay,
  parseNumberInput,
  type JobListingFieldValue,
} from 'src/lib/jobListings.utils'
import PlacesAutocomplete from 'src/pages/ApplicantDetailsPage/tabs/ApplicationTab/components/AddressInput/PlacesAutocomplete'

import JobDescriptionEditor from './JobDescriptionEditor'
import JobListingEmployeesAssignmentSectionWithPopUpMenu from './JobListingEmployeesAssignmentSectionWithPopUpMenu'

interface JobListingOverviewProps {
  job: any
  isEditMode: boolean
  selectedLeadEmployees: any[]
  setSelectedLeadEmployees: (employees: any[]) => void
  selectedMemberEmployees: any[]
  setSelectedMemberEmployees: (employees: any[]) => void
  onFieldChange?: (field: string, value: JobListingFieldValue) => void
}

const JobListingOverview = ({
  job,
  isEditMode,
  selectedLeadEmployees,
  setSelectedLeadEmployees,
  selectedMemberEmployees,
  setSelectedMemberEmployees,
  onFieldChange,
}: JobListingOverviewProps) => {
  const { currentUser } = useAuth()
  const isAdmin = currentUser?.roles?.includes('ADMIN')
  // Fetch employees for job listing assignment based on role
  // Admins can assign both ADMIN and RECRUITER employees
  // Recruiters can only assign RECRUITER employees
  const roleTypesFilter = isAdmin ? ['ADMIN', 'RECRUITER'] : ['RECRUITER']

  const { data: jobListingEmployeesData } = useQuery(GET_EMPLOYEES_ONLY, {
    variables: {
      pageInfo: { offset: 0, limit: 100 },
      filters: { roleTypes: roleTypesFilter },
    },
    fetchPolicy: 'cache-and-network',
  })

  const jobListingEmployees = useMemo(() => {
    return (
      jobListingEmployeesData?.employees?.items?.map((employee) => {
        const name = `${employee.firstName} ${employee.lastName}`
        const isInactive = employee.status !== EmployeeStatus.ACTIVE
        const isAdminRole = employee.roleType === 'ADMIN'

        let label = name
        if (isAdminRole) {
          label = `${name} (Admin)`
        }
        if (isInactive) {
          label = isAdminRole
            ? `${name} (Admin, Inactive)`
            : `${name} (Inactive)`
        }

        return {
          label,
          value: employee.employeeId,
          status: employee.status,
        }
      }) ?? []
    )
  }, [jobListingEmployeesData])

  // Fetch job listing templates for the dropdown
  const { data: templatesData } = useQuery(GET_JOB_LISTING_TEMPLATES, {
    variables: { status: 'ACTIVE' },
  })

  const templateOptions = useMemo(() => {
    const templates = templatesData?.jobListingTemplates || []
    return templates.map((template) => ({
      value: template.jobListingTemplateId,
      label: template.name,
    }))
  }, [templatesData?.jobListingTemplates])

  // Combine job listing employees with any selected employees that might not be in the list
  const employeeOptions = useMemo(() => {
    const recruiterMap = new Map()

    // Add all employees from job listing query
    jobListingEmployees?.forEach((employee) => {
      recruiterMap.set(employee.value, employee)
    })

    // Add any selected lead employees that aren't in the job listing employees
    selectedLeadEmployees?.forEach((selectedEmployee) => {
      if (!recruiterMap.has(selectedEmployee.value)) {
        // Determine if this recruiter is inactive based on status field
        // Only consider as inactive if status exists and is explicitly not ACTIVE
        const isInactive =
          selectedEmployee.status !== undefined &&
          selectedEmployee.status !== EmployeeStatus.ACTIVE

        // Only add (Inactive) if the recruiter is inactive and it's not already in the label
        const label =
          isInactive && !selectedEmployee.label.endsWith('(Inactive)')
            ? `${selectedEmployee.label} (Inactive)`
            : selectedEmployee.label

        recruiterMap.set(selectedEmployee.value, {
          label,
          value: selectedEmployee.value,
          status: selectedEmployee.status, // Preserve the status field
        })
      }
    })

    // Add any selected member employees that aren't in the list
    selectedMemberEmployees?.forEach((selectedEmployee) => {
      if (!recruiterMap.has(selectedEmployee.value)) {
        const isInactive =
          selectedEmployee.status !== undefined &&
          selectedEmployee.status !== EmployeeStatus.ACTIVE
        const label =
          isInactive && !selectedEmployee.label.endsWith('(Inactive)')
            ? `${selectedEmployee.label} (Inactive)`
            : selectedEmployee.label
        recruiterMap.set(selectedEmployee.value, {
          label,
          value: selectedEmployee.value,
          status: selectedEmployee.status,
        })
      }
    })

    return Array.from(recruiterMap.values())
  }, [jobListingEmployees, selectedLeadEmployees, selectedMemberEmployees])

  // Local form state for editing
  const [formValues, setFormValues] = useState(() => buildJobListingFormValues(job))

  // Sync from the job listing only while viewing. While editing, cache
  // refreshes (e.g. adding a tracking link) must not wipe in-progress edits.
  useEffect(() => {
    if (!isEditMode) {
      setFormValues(buildJobListingFormValues(job))
    }
  }, [isEditMode, job])

  const handleFormChange = (field: string, value: JobListingFieldValue) => {
    setFormValues((prev) => ({ ...prev, [field]: value }))
    if (onFieldChange) {
      onFieldChange(field, value)
    }
  }

  // Validation helpers
  const isTitleInvalid = isEditMode && formValues.title.trim().length === 0
  const areEmployeesInvalid = isEditMode && selectedLeadEmployees.length === 0

  const payEdited =
    (formValues.payMin ?? null) !== (job?.payMin ?? null) ||
    (formValues.payMax ?? null) !== (job?.payMax ?? null)

  const isPayInvalid =
    isEditMode &&
    payEdited &&
    formValues.payMin != null &&
    formValues.payMax != null &&
    formValues.payMax < formValues.payMin

  const isDriver = formValues.type === JobListingType.DRIVER

  return (
    <>
      <Text size="xs" fw={400} className="text-doubleNickel-gray-500">
        OVERVIEW
      </Text>
      <div className="space-y-3">
        {/* Title Field */}
        <div className="flex flex-row items-center justify-between">
          <Text size="xs" fw={300} className="flex-shrink-0">
            Title
          </Text>
          {isEditMode ? (
            <div className="w-[250px]">
              <TextInput
                placeholder="Enter job title"
                size="xs"
                value={formValues.title}
                onChange={(e) => handleFormChange('title', e.target.value)}
                error={isTitleInvalid}
                classNames={{
                  input: 'text-xs text-right',
                }}
              />
              {isTitleInvalid && (
                <Text size="xs" className="mt-1 text-right text-red-500">
                  Title is required
                </Text>
              )}
            </div>
          ) : (
            <Text
              size="xs"
              fw={400}
              className="w-[250px] overflow-auto py-1.5 text-right"
            >
              {job?.title || '-'}
            </Text>
          )}
        </div>

        {/* Subtitle Field */}
        <div className="flex flex-row items-center justify-between">
          <Text size="xs" fw={300} className="flex-shrink-0">
            Subtitle
          </Text>
          {isEditMode ? (
            <div className="w-[250px]">
              <TextInput
                placeholder="Short tagline"
                size="xs"
                value={formValues.subtitle}
                onChange={(e) => handleFormChange('subtitle', e.target.value)}
                classNames={{
                  input: 'text-xs text-right',
                }}
              />
            </div>
          ) : (
            <Text
              size="xs"
              fw={400}
              className="w-[250px] overflow-auto py-1.5 text-right"
            >
              {job?.subtitle || '-'}
            </Text>
          )}
        </div>

        {/* Type Field */}
        <div className="flex flex-row items-center justify-between">
          <Text size="xs" fw={300} className="flex-shrink-0">
            Type
          </Text>
          {isEditMode ? (
            <div className="w-[250px]">
              <Select
                data={jobListingTypeOptions}
                value={formValues.type || job?.type}
                onChange={(type) => handleFormChange('type', type)}
                size="xs"
                classNames={{
                  input: 'text-xs text-right',
                }}
              />
            </div>
          ) : (
            <Text
              size="xs"
              fw={400}
              className="w-[250px] overflow-auto py-1.5 text-right"
            >
              {formatEnum(job?.type) || '-'}
            </Text>
          )}
        </div>

        {/* Template Field */}
        <div className="flex flex-row items-center justify-between">
          <Text size="xs" fw={300} className="flex-shrink-0">
            Template
          </Text>
          {isEditMode ? (
            <div className="w-[250px]">
              <Select
                data={templateOptions}
                value={formValues.jobListingTemplateId || ''}
                onChange={(templateId) =>
                  handleFormChange('jobListingTemplateId', templateId || null)
                }
                size="xs"
                clearable
                placeholder="No template"
                classNames={{
                  input: 'text-xs text-right',
                }}
              />
            </div>
          ) : (
            <Text
              size="xs"
              fw={400}
              className="w-[250px] overflow-auto py-1.5 text-right"
            >
              {job?.jobListingTemplate?.name || '-'}
            </Text>
          )}
        </div>

        {/* Location Field */}
        <div className="flex flex-row items-center gap-4">
          <Text size="xs" fw={300} className="flex-shrink-0">
            Location
          </Text>
          {isEditMode ? (
            <div className="w-[250px] flex-1">
              <PlacesAutocomplete
                placeholder="Enter address"
                size="xs"
                value={formValues.address}
                handleChange={(address) => handleFormChange('address', address)}
                classNames={{
                  input: 'text-xs text-right',
                }}
              />
            </div>
          ) : (
            <Text
              size="xs"
              fw={400}
              className="flex-1 overflow-auto py-1.5 text-right"
            >
              {formatAddress(job?.address) || '-'}
            </Text>
          )}
        </div>

        {/* Employees Field */}
        <div className="flex flex-row items-center gap-4">
          <div className="flex-1">
            <JobListingEmployeesAssignmentSectionWithPopUpMenu
              leadEmployees={selectedLeadEmployees}
              memberEmployees={selectedMemberEmployees}
              employeeOptions={employeeOptions}
              isEditable={isEditMode}
              onLeadEmployeesChange={(nextLeadEmployeeIds) => {
                const employeeMap = new Map(
                  employeeOptions.map((employee) => [employee.value, employee])
                )
                const nextLeadEmployees = nextLeadEmployeeIds.map(
                  (employeeId) =>
                    employeeMap.get(employeeId) || {
                      label: employeeId,
                      value: employeeId,
                    }
                )
                setSelectedLeadEmployees(nextLeadEmployees)

                const nextMemberEmployeeIds = selectedMemberEmployees
                  .map((employee) => employee.value)
                  .filter(
                    (employeeId) => !nextLeadEmployeeIds.includes(employeeId)
                  )
                if (
                  nextMemberEmployeeIds.length !== selectedMemberEmployees.length
                ) {
                  const nextMemberEmployees = nextMemberEmployeeIds.map(
                    (employeeId) =>
                      employeeMap.get(employeeId) || {
                        label: employeeId,
                        value: employeeId,
                      }
                  )
                  setSelectedMemberEmployees(nextMemberEmployees)
                  if (onFieldChange) {
                    onFieldChange('memberEmployeeIds', nextMemberEmployeeIds)
                  }
                }

                if (onFieldChange) {
                  onFieldChange('recruiters', nextLeadEmployeeIds)
                }
              }}
              onMemberEmployeesChange={(nextMemberEmployeeIds) => {
                const leadEmployeeIds = selectedLeadEmployees.map(
                  (employee) => employee.value
                )
                const filteredMemberEmployeeIds = nextMemberEmployeeIds.filter(
                  (employeeId) => !leadEmployeeIds.includes(employeeId)
                )
                const employeeMap = new Map(
                  employeeOptions.map((employee) => [employee.value, employee])
                )
                const nextMemberEmployees = filteredMemberEmployeeIds.map(
                  (employeeId) =>
                    employeeMap.get(employeeId) || {
                      label: employeeId,
                      value: employeeId,
                    }
                )
                setSelectedMemberEmployees(nextMemberEmployees)
                if (onFieldChange) {
                  onFieldChange('memberEmployeeIds', filteredMemberEmployeeIds)
                }
              }}
            />
            {areEmployeesInvalid && (
              <Text size="xs" className="mt-1 text-right text-red-500">
                At least one lead employee must be assigned
              </Text>
            )}
          </div>
        </div>
      </div>

      <Divider variant="dashed" />
      <CollapsibleSection title="APPLICATION">
        <div className="space-y-3">
          <div className="flex flex-row items-center justify-between">
            <Text
              size="xs"
              fw={300}
              className="flex flex-shrink-0 items-center gap-1"
            >
              Min. employment history
              <Tooltip
                multiline
                w={260}
                withArrow
                label="Applicants must account for this many years of history, including gaps. Leave empty for no requirement."
              >
                <span className="inline-flex cursor-help items-center">
                  <IconInfoCircle className="h-3.5 w-3.5 fill-none stroke-doubleNickel-gray-400" />
                </span>
              </Tooltip>
            </Text>
            {isEditMode ? (
              <div className="w-[250px]">
                <NumberInput
                  value={formValues.minEmploymentHistoryYears ?? ''}
                  onChange={(value) =>
                    handleFormChange(
                      'minEmploymentHistoryYears',
                      parseNumberInput(value)
                    )
                  }
                  placeholder="Years"
                  min={1}
                  hideControls
                  size="xs"
                  classNames={{ input: 'text-xs text-right' }}
                />
              </div>
            ) : (
              <Text size="xs" fw={400} className="w-[250px] py-1.5 text-right">
                {formValues.minEmploymentHistoryYears != null
                  ? `${formValues.minEmploymentHistoryYears} years`
                  : '-'}
              </Text>
            )}
          </div>
        </div>
      </CollapsibleSection>

      <Divider variant="dashed" />
      <CollapsibleSection title="COMPENSATION">
        <div className="space-y-3">
          <div className="flex flex-row items-center justify-between">
            <Text size="xs" fw={300} className="flex-shrink-0">
              Employment
            </Text>
            {isEditMode ? (
              <div className="w-[250px]">
                <Select
                  data={employmentTypeOptions}
                  value={formValues.employmentType}
                  onChange={(value) =>
                    handleFormChange('employmentType', value)
                  }
                  placeholder="Select type"
                  size="xs"
                  clearable
                  classNames={{ input: 'text-xs text-right' }}
                />
              </div>
            ) : (
              <Text size="xs" fw={400} className="w-[250px] py-1.5 text-right">
                {formValues.employmentType
                  ? EmploymentTypeLabels[formValues.employmentType]
                  : '-'}
              </Text>
            )}
          </div>

          <div className="flex flex-row items-start justify-between">
            <Text size="xs" fw={300} className="flex-shrink-0 pt-1.5">
              Pay
            </Text>
            {isEditMode ? (
              <div className="w-[250px]">
                <div className="flex flex-row gap-2">
                <NumberInput
                  className="flex-1"
                  value={formValues.payMin ?? ''}
                  onChange={(value) =>
                    handleFormChange(
                      'payMin',
                      parseNumberInput(value)
                    )
                  }
                  placeholder="Min"
                  min={0}
                  decimalScale={2}
                  prefix="$"
                  hideControls
                  size="xs"
                  classNames={{ input: 'text-xs' }}
                />
                <NumberInput
                  className="flex-1"
                  value={formValues.payMax ?? ''}
                  onChange={(value) =>
                    handleFormChange(
                      'payMax',
                      parseNumberInput(value)
                    )
                  }
                  placeholder="Max"
                  min={0}
                  decimalScale={2}
                  prefix="$"
                  hideControls
                  size="xs"
                  classNames={{ input: 'text-xs' }}
                />
                <Select
                  className="w-[92px]"
                  data={payUnitOptions}
                  value={formValues.payUnit}
                  onChange={(value) => handleFormChange('payUnit', value)}
                  placeholder="Unit"
                  size="xs"
                  clearable
                  classNames={{ input: 'text-xs' }}
                />
                </div>
                {isPayInvalid && (
                  <Text size="xs" className="mt-1 text-right text-red-500">
                    Max pay must be greater than or equal to min pay
                  </Text>
                )}
              </div>
            ) : (
              <Text size="xs" fw={400} className="w-[250px] py-1.5 text-right">
                {formatPay(formValues)}
              </Text>
            )}
          </div>

          <div className="flex flex-row items-start justify-between">
            <Text size="xs" fw={300} className="flex-shrink-0 pt-1.5">
              Benefits
            </Text>
            {isEditMode ? (
              <div className="w-[250px]">
                <TagsInput
                  value={formValues.benefits}
                  onChange={(value) => handleFormChange('benefits', value)}
                  placeholder="Add a benefit and press Enter"
                  size="xs"
                  classNames={{ input: 'text-xs' }}
                />
              </div>
            ) : (
              <Text size="xs" fw={400} className="w-[250px] py-1.5 text-right">
                {formValues.benefits?.length
                  ? formValues.benefits.join(', ')
                  : '-'}
              </Text>
            )}
          </div>
        </div>
      </CollapsibleSection>

      {isDriver && (
        <>
          <Divider variant="dashed" />
          <CollapsibleSection title="DRIVER REQUIREMENTS">
            <div className="space-y-3">
              <div className="flex flex-row items-start justify-between">
                <Text size="xs" fw={300} className="flex-shrink-0 pt-1.5">
                  CDL class
                </Text>
                {isEditMode ? (
                  <div className="w-[250px]">
                    <MultiSelect
                      data={licenseClassOptions}
                      value={formValues.licenseClass}
                      onChange={(value) =>
                        handleFormChange('licenseClass', value)
                      }
                      placeholder="Select classes"
                      size="xs"
                      clearable
                      classNames={{ input: 'text-xs' }}
                    />
                  </div>
                ) : (
                  <Text
                    size="xs"
                    fw={400}
                    className="w-[250px] py-1.5 text-right"
                  >
                    {formValues.licenseClass?.length
                      ? formValues.licenseClass
                          .map((value) => LicenseClassLabels[value] ?? value)
                          .join(', ')
                      : '-'}
                  </Text>
                )}
              </div>

              <div className="flex flex-row items-start justify-between">
                <Text size="xs" fw={300} className="flex-shrink-0 pt-1.5">
                  Endorsements
                </Text>
                {isEditMode ? (
                  <div className="w-[250px]">
                    <MultiSelect
                      data={endorsementOptions}
                      value={formValues.endorsements}
                      onChange={(value) =>
                        handleFormChange('endorsements', value)
                      }
                      placeholder="Select endorsements"
                      size="xs"
                      clearable
                      classNames={{ input: 'text-xs' }}
                    />
                  </div>
                ) : (
                  <Text
                    size="xs"
                    fw={400}
                    className="w-[250px] py-1.5 text-right"
                  >
                    {formValues.endorsements?.length
                      ? formValues.endorsements.join(', ')
                      : '-'}
                  </Text>
                )}
              </div>

              <div className="flex flex-row items-center justify-between">
                <Text size="xs" fw={300} className="flex-shrink-0">
                  Min. experience
                </Text>
                {isEditMode ? (
                  <div className="w-[250px]">
                    <NumberInput
                      value={formValues.minMonthsExperience ?? ''}
                      onChange={(value) =>
                        handleFormChange(
                          'minMonthsExperience',
                          parseNumberInput(value)
                        )
                      }
                      placeholder="Months"
                      min={0}
                      hideControls
                      size="xs"
                      classNames={{ input: 'text-xs text-right' }}
                    />
                  </div>
                ) : (
                  <Text
                    size="xs"
                    fw={400}
                    className="w-[250px] py-1.5 text-right"
                  >
                    {formValues.minMonthsExperience != null
                      ? `${formValues.minMonthsExperience} months`
                      : '-'}
                  </Text>
                )}
              </div>
            </div>
          </CollapsibleSection>
        </>
      )}

      <Divider variant="dashed" />
      <CollapsibleSection title="DESCRIPTION">
        {isEditMode ? (
          <JobDescriptionEditor
            value={formValues.description}
            onChange={(html) => handleFormChange('description', html)}
          />
        ) : formValues.description ? (
          <JobDescriptionEditor value={formValues.description} editable={false} />
        ) : (
          <Text size="xs" fw={400} className="text-doubleNickel-gray-700">
            -
          </Text>
        )}
      </CollapsibleSection>
    </>
  )
}

export default JobListingOverview
